import "../../styles/MegaEditor.css";
import "../../styles/MegaEditor.dark.css";
import { useRef, useEffect, useContext, useState, useMemo } from "react";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import { useDarkMode } from "../../common/contexts/GlobalContext";
import { getScriptTemplatesByDistro } from "../functionLibraries/FunctionLibraryTemplates";
import { getCodeLegendById } from "../../api/APIHelpers";
import CodeLegendSelector from "./CodeLegendSelector";
import { CodeLegendContext } from "../../common/contexts/CodeLegendContext";
import {
  addLookupValueFieldsToTag,
  generateSafeAlarmIds,
  getLookupIDs,
  getLookupValues,
  getParentTagCandidates,
  parentFieldsAfterDialogSave,
  resolveParentTagIdentifier,
} from "../helpers/Helpers";
import { hasValue } from "../../common/helpers/Utils";
import { applyDialogResult } from "../helpers/TagDialogFields";
import { UniversalDescriptorContext } from "../../common/contexts/UniversalDescriptorContext";

const divBase = (divRef) => {
  return (
    <div>
      <div
        id='common-tag-container'
        className='dialog-content tag-conf'
        title='Tag Configuration'
        ref={divRef}></div>
    </div>
  );
};

export const SharedTagDialog = ({
  open,
  setOpen,
  tag,
  updateTag,
  setLoading,
  setInfoMessages,
  distro,
  existingTags,
  setTag,
}) => {
  const divRef = useRef(null);
  const lookupValues = useContext(LookupValueContext);
  const { codeLegends } = useContext(CodeLegendContext);
  const { universalDescriptors } = useContext(UniversalDescriptorContext);
  const darkMode = useDarkMode();

  // State for code legend modal
  const [openCodeLegendSelect, setOpenCodeLegendSelect] = useState(false);

  const [selectedCodeLegend, setSelectedCodeLegend] = useState(null);

  const codeLegendModal = useMemo(() => {
    return (
      <CodeLegendSelector
        open={openCodeLegendSelect}
        onClose={(value) => {
          setOpenCodeLegendSelect(false);
          setSelectedCodeLegend(value);
          window
            .$('input[id="serverProperties.CodeLegendId"]')
            .val(value.value);
          window
            .$('input[id="serverProperties.CodeLegendId"]')
            .attr("input-value", value.id);
        }}
        selectedCodeLegend={selectedCodeLegend}
        setSelectedCodeLegend={setSelectedCodeLegend}
      />
    );
  }, [openCodeLegendSelect, selectedCodeLegend]);

  useEffect(() => {
    if (open) {
      const buildTagTypeDictionary = function (lookupName) {
        let lookupItems = {};
        for (let i = 0; i < lookupValues.length; i++) {
          let lk = lookupValues[i];
          if (lk.LookupName === lookupName) {
            for (let j = 0; j < lk.Values.length; j++) {
              lookupItems[lk.Values[j].LookupValueId] = {
                InternalName: lk.Values[j].InternalName,
                DisplayName: lk.Values[j].DisplayName,
              };
            }
          }
        }
        return lookupItems;
      };

      const getChoiceDictionary = function (lookupName) {
        if (lookupName === "AssetNumbers") {
          return {};
        } else {
          return buildTagTypeDictionary(lookupName);
        }
      };

      const lookupNameToId = function (value, category) {
        return getLookupIDs([value], category, lookupValues)[0];
      };

      const lookupIdToName = function (id, category) {
        let name = getLookupValues(category, id, lookupValues);
        if (Array.isArray(name)) {
          //For some reason a blank array is returned if ID not found
          return "";
        } else {
          return name;
        }
      };

      const blocker = {
        block: function (_) {
          setLoading(true);
        },
        unblock: function () {
          setLoading(false);
        },
      };

      const getCodeLegendTitle = (id) => {
        const legend = codeLegends.find(
          (cl) => cl.id.toString() === id.toString()
        );
        return legend.label;
      };

      const getDescForCodeLegendId = (id, input) => {
        if (codeLegends.length > 1) {
          input.val(getCodeLegendTitle(id));
        } else {
          let codeLegend = getCodeLegendById(id);
          input.val(codeLegend.data.Title);
        }
      };

      const shownParent = resolveParentTagIdentifier(tag, existingTags);

      // Propagates changes made in the shared tag dialog to the tag grid
      const saveTagRecord = (unconvertedTag, success, failure) => {
        let convertedTag =
          window.Enbase.Common.Tag.Normalization.ConvertTagFromHubInterfaceToDashboard(
            unconvertedTag
          );

        const parentFields = parentFieldsAfterDialogSave(
          tag,
          shownParent,
          unconvertedTag.parentTagId
        );
        delete convertedTag.ParentAssetTagId;
        delete convertedTag.ParentTagIdentifier;
        Object.assign(convertedTag, parentFields);

        try {
          // Reset fields that should not be changed that may have been changed in the tag object
          if (tag.TCTagLayerId) {
            convertedTag.TCTagLayerId = tag.TCTagLayerId;
          } else {
            convertedTag.TagId = tag.TagId;
          }
          convertedTag.AssetId = tag.AssetId;
          convertedTag.AssetNumber = tag.AssetNumber;
          // Put something valid in DisplayFormat so that if the tag is reopened it shows as a Text display tag, not number
          if (convertedTag.CodeLegendId && !convertedTag.DisplayFormat) {
            convertedTag.DisplayFormat = "{1,No Fault,N}";
          }
          convertedTag = applyDialogResult(
            tag,
            addLookupValueFieldsToTag(convertedTag, lookupValues)
          );
          updateTag(convertedTag);
          setTag(convertedTag);
          success(convertedTag.AssetTagId);
          const msg = {
            type: "snackbar",
            open: true,
            severity: "info",
            message: "Changes saved to grid.",
          };
          setInfoMessages(msg);
        } catch (e) {
          failure(convertedTag);
          const errNotice = {
            type: "snackbar",
            open: true,
            severity: "error",
            message: "An error occurred when saving tag changes to the grid.",
          };
          if (e.message?.toLowerCase().includes("network")) {
            errNotice.message =
              "The application has lost connection to the internet.";
          }
          setInfoMessages(errNotice);
        }
      };

      const showServerLegendEditor = (_) => {
        let selectedLegend = codeLegends.find(
          (cl) =>
            cl.id.toString() ===
            window
              .$('input[id="serverProperties.CodeLegendId"]')
              .attr("input-value")
        );
        setSelectedCodeLegend(selectedLegend);
        setOpenCodeLegendSelect(true);
      };

      const parentTagList = getParentTagCandidates(tag, existingTags).reduce(
        (parentTags, candidate) => {
          parentTags[candidate.TagIdentifier] = {
            InternalName: candidate.TagName,
            DisplayName: candidate.TagName,
          };
          return parentTags;
        },
        {}
      );
      const hasNoValidParentOptions = Object.keys(parentTagList).length === 0;
      const currentParentNotInList =
        hasValue(shownParent) &&
        !Object.prototype.hasOwnProperty.call(
          parentTagList,
          shownParent.toString()
        );
      const shouldUseFreeTextParentInput =
        hasNoValidParentOptions || currentParentNotInList;

      // _contextOptions in the shared tag dialog
      const options = {
        DistributionVersion: parseFloat(distro), // Some functionality locked behind 3.1
        dom: window.$(divRef.current),
        DeviceStatus: null,
        AppOutputs: null,
        SelectMenus: false,
        SaveTagRecord: saveTagRecord,
        DeleteTagRecord: () => {},
        canManageAppAccess: false,
        FunctionAccessLevel: 75,
        MainDialogZIndex: null,
        RunningInDashboard: true,
        canManageServerProperties: true,
        ShowServerStrappingTableEditor: null, //Only called in server code
        ShowServerLegendEditor: showServerLegendEditor, //Only called in server code
        GetDescForCodeLegendId: getDescForCodeLegendId,
        GetDescForStrappingTableId: null, //Only called in server code
        IsChemicalView: false,
        IsReadOnly: false,
        ShowSaveButton: true,
        allowTagDelete: false,
        GetChoiceDictionary: getChoiceDictionary,
        ExistingTags: existingTags.map((tag) => {
          return {
            RecordId: tag.AssetTagId,
            AssetNumber: tag.AssetNumber,
            TagId: tag.TagIdentifier,
            TagName: tag.TagName,
            DynamicProperties: tag.DynamicProperties,
            UniversalDescriptor: tag.UniversalDescriptor,
          };
        }),
        LookupIdToName: lookupIdToName,
        LookupNameToId: lookupNameToId,
        ParentTagList: parentTagList,
        OnDialogClose: () => {
          setOpen(false);
        },
        Blocker: blocker,
        CalculatedTagTemplates: getScriptTemplatesByDistro(distro),
        TCMode: true,
        universalDescriptorOptions: universalDescriptors
          .filter((ud) => ud.IsActive)
          .map((ud) => ud.UD),
      };

      let convertedTag =
        window.Enbase.Common.Tag.Normalization.ConvertTagFromDashboardToHubInterface(
          tag,
          lookupNameToId
        );
      const dialog = new window.Enbase.Common.Tag.MegaEditor(options);

      // Prepare tag object for the shared tag dialog
      convertedTag.assetNumber = tag.AssetNumber;
      convertedTag.tagId = tag.TagIdentifier;
      // Enbase maps ParentAssetTagId (an AssetTagId) into parentTagId, but the field holds a Tag
      // Identifier.
      convertedTag.parentTagId = shownParent;
      if (tag.SourceType && !convertedTag.sourceType) {
        convertedTag.sourceType = lookupNameToId(
          tag.SourceType,
          "TagSourceType"
        );
      }

      if (tag.DataType && !convertedTag.dataType) {
        convertedTag.dataType = lookupNameToId(tag.DataType, "TagDataType");
      }

      if (!tag.AlarmType) {
        convertedTag.alarmType = null;
      } else {
        convertedTag.alarmType = lookupNameToId(tag.AlarmType, "TagAlarmType");
      }

      if (!convertedTag.assetNumber) {
        convertedTag.assetNumber = "Tag Configurator";
      }

      if (tag.TagIdentifier) {
        const { safeAlarmId, safeAlarm2Id } = generateSafeAlarmIds(
          tag,
          existingTags
        );
        convertedTag.alarmId = safeAlarmId;
        convertedTag.alarm2Id = safeAlarm2Id;
      }

      if (dialog.initialized) {
        dialog.show(convertedTag);

        // Add scoped class to the jQuery UI dialog wrapper for main dialog
        const $mainDialogWrapper = window
          .$(divRef.current)
          .closest(".ui-dialog");
        if ($mainDialogWrapper.length) {
          $mainDialogWrapper.addClass("shared-tag-dialog");
          if (darkMode) {
            $mainDialogWrapper.addClass("dark");
            window.$(divRef.current).addClass("dark");
          } else {
            $mainDialogWrapper.removeClass("dark");
            window.$(divRef.current).removeClass("dark");
          }
        }

        window.$(document).on("dialogopen.sharedTag", function (event) {
          const $wrapper = window.$(event.target).closest(".ui-dialog");
          if ($wrapper.find(".property-info").length) {
            $wrapper.addClass("shared-tag-table-dialog");
          }
          // Mark Calculate Gain/Offset dialog
          if ($wrapper.find(".tag-calc-offset").length) {
            if (darkMode) {
              $wrapper.addClass("dark");
              window.$(event.target).addClass("dark");
            }
          }
        });

        // Disable fields incompatible with the tag dialog
        window.$("input#assetNumber").val(convertedTag.assetNumber);
        window.$('button:contains("Delete Tag")').prop("disabled", true);
        window.$('button:contains("Delete Tag")').remove();
        window
          .$('button:contains("Edit Strapping Table")')
          .prop("disabled", true);
        window.$("input#assetNumber").prop("disabled", true);
        // window.$('select#alarm2Type').prop('disabled', true);
        // window.$('input[value="Choose Code Legend"]').prop('disabled', true);
        window.$('button:contains("Edit Code Legend")').parent().remove();
        window.$('[id*="-placeholder-"]').parent().remove();
        window.$('button:contains("Export Function")').prop("disabled", true);
        window.$('button:contains("Import Function")').prop("disabled", true);
        window.$("input#tagId").prop("disabled", false); // Not sure if we want to do this

        if (shouldUseFreeTextParentInput) {
          const $parentSelect = window.$(divRef.current).find("#parentTagId");
          if ($parentSelect.length && $parentSelect.is("select")) {
            const $freeTextInput = window.$(
              '<input type="text" id="parentTagId" name="parentTagId" />'
            );
            $freeTextInput.val(
              hasValue(convertedTag.parentTagId) ? convertedTag.parentTagId : ""
            );
            $freeTextInput.attr(
              "title",
              "Enter a Tag Identifier (a number) for the parent tag. A matching tag name could not be shown in the available parent tag options."
            );
            $freeTextInput.attr("placeholder", "Tag Identifier");
            $parentSelect.replaceWith($freeTextInput);
          }
        }

        // Enable alarm fields
        window.$("input#a1l-alarmId").prop("disabled", false);
        window.$("input#a1vc-alarmId").prop("disabled", false);
        window.$("input#a2vc-alarm2Id").prop("disabled", false);
        window.$("input#a2l-alarm2Id").prop("disabled", false);

        window.$("input#serverProperties\\.SyncToHub").prop("disabled", false);
      }

      return () => {
        if (open === false) {
          window.$("#dialog-content").remove();
          // Remove namespaced dialogopen handler to avoid leaks
          window.$(document).off("dialogopen.sharedTag");
          return divBase(divRef);
        }
      };
    }
  }, [
    open,
    tag,
    setOpen,
    updateTag,
    lookupValues,
    setLoading,
    setInfoMessages,
    distro,
    existingTags,
    codeLegends,
    setTag,
    darkMode,
    universalDescriptors,
  ]);

  return (
    <>
      {divBase(divRef)}
      {codeLegendModal}
    </>
  );
};
